// Single source of truth for Indonesian phone-number handling across the
// backoffice. The UI shows a static "+62" prefix and the user types only the
// national part (no leading 0); we store the value in the database as
// "+62xxxxxxxx".

export const PHONE_COUNTRY_CODE = '+62'

// National part length bounds (digits AFTER the +62 prefix, no leading 0).
export const PHONE_MIN_DIGITS = 8
export const PHONE_MAX_DIGITS = 13

// Allowed keystrokes inside the national-number field: digits only, and the
// first digit can never be 0. Passed to MyTextField's `validationRegex`, which
// drops any keystroke that would make the value fail this test (empty is
// allowed so the field can still be cleared).
export const PHONE_INPUT_REGEX = /^[1-9][0-9]*$/

// Supporting text shown under the field.
export const PHONE_HELPER_TEXT =
  'Type the number without the leading 0 — e.g. 81234567890'

// Strip any country-code / leading-zero noise and return the national part
// only. Accepts "+6281...", "6281...", "081...", "81..." → "81...". Used when
// prefilling a form from a stored/legacy value.
export function toNationalPhone(raw) {
  let digits = String(raw ?? '').replace(/\D/g, '')
  if (!digits) return ''
  if (digits.startsWith('62')) digits = digits.slice(2)
  digits = digits.replace(/^0+/, '')
  return digits
}

// National part → canonical storage value "+62xxxxxxxx". Empty stays empty so
// optional fields submit nothing rather than a bare "+62".
export function toE164Phone(national) {
  const digits = toNationalPhone(national)
  return digits ? `${PHONE_COUNTRY_CODE}${digits}` : ''
}

// yup schema factory for the national part. Pass the page's own `yup` import so
// this stays decoupled from the yup version each form uses.
export function nationalPhoneSchema(yup, { required = true } = {}) {
  const schema = yup
    .string()
    .matches(PHONE_INPUT_REGEX, {
      message: 'Enter a valid number without the leading 0',
      excludeEmptyString: true,
    })
    .min(PHONE_MIN_DIGITS, `Phone must be at least ${PHONE_MIN_DIGITS} digits`)
    .max(PHONE_MAX_DIGITS, `Phone must not exceed ${PHONE_MAX_DIGITS} digits`)
  return required
    ? schema.required('Phone number is required')
    : schema.nullable()
}

// Validate a national-part value and return an error string (or null when
// valid). For controlled forms that track errors manually instead of via yup.
export function getNationalPhoneError(value, { required = true } = {}) {
  const digits = String(value ?? '')
  if (!digits) return required ? 'Phone number is required' : null
  if (!PHONE_INPUT_REGEX.test(digits)) {
    return 'Enter a valid number without the leading 0'
  }
  if (digits.length < PHONE_MIN_DIGITS) {
    return `Phone must be at least ${PHONE_MIN_DIGITS} digits`
  }
  if (digits.length > PHONE_MAX_DIGITS) {
    return `Phone must not exceed ${PHONE_MAX_DIGITS} digits`
  }
  return null
}
